import React, { useState, useEffect } from 'react';
import { Student } from '../../types/student';
import { Coach } from '../../types/coach';
import { School } from '../../types/school';
import { CoachesService } from '../../services/coachesService';
import { SchoolsService } from '../../services/schoolsService';
import Modal from '../../Common/Components/Modal';
import { toast } from 'react-toastify';

interface EditStudentDialogProps {
  student: Student;
  onClose: () => void;
  onSave: (updatedStudent: Student) => Promise<void>;
  show: boolean;
}

const EditStudentDialog: React.FC<EditStudentDialogProps> = ({
  student,
  onClose,
  onSave,
  show
}) => {
  const [fullName, setFullName] = useState(student.fullName);
  const [selectedSchoolId, setSelectedSchoolId] = useState(student.schoolId || '');
  const [isActive, setIsActive] = useState(!student.deleted);
  const [selectedCoachId, setSelectedCoachId] = useState(student.coachId || '');
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [schools, setSchools] = useState<School[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCoaches, setIsLoadingCoaches] = useState(true);
  const [isLoadingSchools, setIsLoadingSchools] = useState(true);

  useEffect(() => {
    if (show) {
      loadCoaches();
      loadSchools();
    }
  }, [show]);

  const loadCoaches = async () => {
    try {
      setIsLoadingCoaches(true);
      const coachesData = await CoachesService.getAllCoaches();
      setCoaches(coachesData);
    } catch (error) {
      console.error('Error loading coaches:', error);
    } finally {
      setIsLoadingCoaches(false);
    }
  };

  const loadSchools = async () => {
    try {
      setIsLoadingSchools(true);
      const schoolsData = await SchoolsService.getAllSchools();
      setSchools(schoolsData);
    } catch (error) {
      console.error('Error loading schools:', error);
    } finally {
      setIsLoadingSchools(false);
    }
  };

  const handleSave = async () => {
    if (!fullName.trim()) {
      toast.error('Please enter a full name');
      return;
    }

    if (!selectedSchoolId) {
      toast.error('Please select a school');
      return;
    }

    if (!selectedCoachId || selectedCoachId === '') {
      toast.error('Please select a coach for the student');
      return;
    }

    setIsLoading(true);
    try {
      const selectedSchool = schools.find(s => s.id === selectedSchoolId);
      const schoolNameToSave = selectedSchool ? selectedSchool.name : student.schoolName;

      const updatedStudent: Student = {
        ...student,
        fullName: fullName.trim(),
        schoolName: schoolNameToSave,
        schoolId: selectedSchoolId,
        coachId: selectedCoachId,
        deleted: !isActive,
      };

      await onSave(updatedStudent);
      onClose();
    } catch (error) {
      console.error('Error saving student:', error);
      toast.error('Failed to update student. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onClose} id="editStudentModal" modal-center="true"
      className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
      dialogClassName="w-[calc(100vw-2rem)] mx-4 md:mx-0 md:w-[30rem] bg-white shadow rounded-md dark:bg-zink-600 flex flex-col h-full">
      <Modal.Header className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-zink-500"
        closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500">
        <Modal.Title className="text-16">Edit Student Details</Modal.Title>
      </Modal.Header>
      <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Full Name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:border-zink-500"
              placeholder="Enter full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">School</label>
            {isLoadingSchools ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
              </div>
            ) : (
              <select
                value={selectedSchoolId}
                onChange={(e) => setSelectedSchoolId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:border-zink-500"
              >
                <option value="" disabled>Select a school</option>
                {schools.map((school) => (
                  <option key={school.id} value={school.id}>
                    {school.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Student Coach</label>
            {isLoadingCoaches ? (
              <div className="flex items-center justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900"></div>
              </div>
            ) : (
              <select
                value={selectedCoachId}
                onChange={(e) => setSelectedCoachId(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:border-custom-500 dark:bg-zink-700 dark:border-zink-500"
              >
                <option value="" disabled>Select a coach</option>
                {coaches.map((coach) => (
                  <option key={coach.id} value={coach.id}>
                    {coach.fullName}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Student Status</label>
            <div className="flex items-center gap-2">
              <span className="text-sm">{isActive ? 'Active' : 'Inactive'}</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
          
        
        
        </div>
      </Modal.Body>
      <Modal.Footer className="flex items-center justify-between p-4 mt-auto border-t border-slate-200 dark:border-zink-500">
        <button
          onClick={onClose}
          className="px-4 py-2 text-sm bg-slate-100 hover:bg-slate-200 dark:bg-zink-600 dark:hover:bg-zink-500 rounded-md transition-colors"
          disabled={isLoading}
        >
          Close
        </button>
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="px-4 py-2 text-sm bg-custom-500 text-white hover:bg-custom-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isLoading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          )}
          Save
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditStudentDialog;